import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Settings as SettingsIcon,
  Message as MessageIcon,
  Business as PropertyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader } from '../components/Layout';
import authService from '../services/authService';
import api from '../services/api';
import MpesaConfig from '../components/Settings/MpesaConfig';
import EmailConfig from '../components/Settings/EmailConfig';
import SmsConfig from '../components/Settings/SmsConfig';
import SystemConfig from '../components/Settings/SystemConfig';
import MessageTemplates from '../components/Settings/MessageTemplates';
import PropertyManagement from '../components/Settings/PropertyManagement';

const Settings = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    username: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setProfileData({
        full_name: currentUser.full_name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        username: currentUser.username || '',
      });
    }
  }, []);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put('/api/auth/profile', {
        full_name: profileData.full_name,
        email: profileData.email,
        phone: profileData.phone,
      });
      
      // Update local storage with new user data
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      console.error(error);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await api.put('/api/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      console.error(error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Check if user is admin (superuser or landlord)
  const isAdmin = user?.role === 'superuser' || user?.role === 'landlord';

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage your account settings and preferences"
        breadcrumbs={[{ label: 'Settings' }]}
      />

      {/* Settings Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tab icon={<PersonIcon />} label="Profile" iconPosition="start" />
          {isAdmin && <Tab icon={<PropertyIcon />} label="Properties" iconPosition="start" />}
          {isAdmin && <Tab icon={<SettingsIcon />} label="System Config" iconPosition="start" />}
          {isAdmin && <Tab icon={<PaymentIcon />} label="M-Pesa" iconPosition="start" />}
          {isAdmin && <Tab icon={<EmailIcon />} label="Email" iconPosition="start" />}
          {isAdmin && <Tab icon={<SmsIcon />} label="SMS" iconPosition="start" />}
          {isAdmin && <Tab icon={<MessageIcon />} label="Messages" iconPosition="start" />}
        </Tabs>
      </Paper>

      {/* Profile Tab */}
      {activeTab === 0 && (
      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <PersonIcon color="primary" />
              <Typography variant="h6">Profile Information</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <form onSubmit={handleProfileSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="full_name"
                    value={profileData.full_name}
                    onChange={handleProfileChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    name="username"
                    value={profileData.username}
                    onChange={handleProfileChange}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Password Change */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <SecurityIcon color="primary" />
              <Typography variant="h6">Change Password</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <form onSubmit={handlePasswordSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="current_password"
                    type="password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    helperText="Password must be at least 8 characters"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Account Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: 40,
                  }}
                >
                  {user?.username?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h6">{user?.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Role: {user?.role?.toUpperCase()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Notifications (placeholder) */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6">Notifications</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Email notification settings coming soon...
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      )}

      {/* Property Management Tab */}
      {isAdmin && activeTab === 1 && <PropertyManagement />}

      {/* System Configuration Tab */}
      {isAdmin && activeTab === 2 && <SystemConfig />}

      {/* M-Pesa Configuration Tab */}
      {isAdmin && activeTab === 3 && <MpesaConfig />}

      {/* Email Configuration Tab */}
      {isAdmin && activeTab === 4 && <EmailConfig />}

      {/* SMS Configuration Tab */}
      {isAdmin && activeTab === 5 && <SmsConfig />}

      {/* Message Templates Tab */}
      {isAdmin && activeTab === 6 && <MessageTemplates />}
    </Box>
  );
};

export default Settings;
